import React, { useState, useEffect } from 'react';
import PageHeader from '../components/common/PageHeader';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import {
  Assessment,
  LocationOn,
  LocalShipping,
  Block,
} from '@mui/icons-material';
import StatCard from '../components/shared/StatCard';
import StatisticsChart, { MonthlyChartData, DailyChartData } from '../components/statistics/StatisticsChart';
import { getStatisticsOverview, StatisticsOverview, exportStatisticsToExcel, exportStatisticsToPdf } from '../services/statisticsApi';
import { CompanyStatusFilter } from '../services/api/companyApi';
import { formatNumber, formatRejectRate } from '../utils/numberFormat';
import { buildDateRangeOptions, buildStatsDailyChartData } from './_shared/ghgStatsUtils';
import ExportButtonMenu from './_shared/ExportButtonMenu';
import PeriodFilterToggle from './_shared/PeriodFilterToggle';
import StatusFilterDropdown from './_shared/StatusFilterDropdown';
import StatsChartSkeleton from './_shared/StatsChartSkeleton';
import StatCardsSkeleton from './_shared/StatCardsSkeleton';
import { StatsRankingSkeletonRow } from './_shared/RankingTableSkeleton';
import { useDateRangeFilter } from './_shared/useDateRangeFilter';

type Order = 'asc' | 'desc';
type OrderBy =
  | 'rank'
  | 'company_name'
  | 'licence_type'
  | 'total_transactions'
  | 'total_geolocations'
  | 'total_unique_shipments'
  | 'disabled'
  | 'partner_name';

type RankedCompany = StatisticsOverview['companies'][number] & { rank: number };

// Stable canonical rank: total_transactions DESC, so the displayed rank stays
// consistent when re-sorting by other columns.
const rankCompanies = (companies?: StatisticsOverview['companies']): RankedCompany[] =>
  companies
    ? [...companies]
        .sort((a, b) => (b.total_transactions || 0) - (a.total_transactions || 0))
        .map((company, idx) => ({ ...company, rank: idx + 1 }))
    : [];

const STRING_SORT_FIELDS = new Set<OrderBy>(['company_name', 'licence_type', 'partner_name']);

const compareCompanies = (a: RankedCompany, b: RankedCompany, orderBy: OrderBy, order: Order): number => {
  if (STRING_SORT_FIELDS.has(orderBy)) {
    const aValue = ((a as any)[orderBy] as string) || '';
    const bValue = ((b as any)[orderBy] as string) || '';
    return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  }

  if (orderBy === 'disabled') {
    // active (disabled=false) sorts higher when desc (default for status column)
    const aValue = a.disabled ? 0 : 1;
    const bValue = b.disabled ? 0 : 1;
    return order === 'asc' ? aValue - bValue : bValue - aValue;
  }

  // Numeric fields (rank, total_transactions, total_geolocations, total_unique_shipments)
  const aValue = ((a as any)[orderBy] as number) || 0;
  const bValue = ((b as any)[orderBy] as number) || 0;
  return order === 'asc' ? aValue - bValue : bValue - aValue;
};

// Aggregate monthly data across all companies by month, sorted by year then month.
const aggregateMonthlyChartData = (monthly?: StatisticsOverview['monthly']): MonthlyChartData[] => {
  if (!monthly) return [];

  const monthlyAggregates: Record<string, MonthlyChartData> = {};

  monthly.forEach((monthData) => {
    const key = `${monthData.year}-${monthData.month}`;
    if (!monthlyAggregates[key]) {
      monthlyAggregates[key] = {
        month: monthData.month_name?.trim() || `Month ${monthData.month}`,
        monthNumber: monthData.month,
        year: monthData.year,
        transactions: 0,
        geolocations: 0,
        uniqueShipments: 0,
      };
    }

    monthData.companies.forEach((company) => {
      monthlyAggregates[key].transactions += Number(company.total_transactions) || 0;
      monthlyAggregates[key].geolocations += Number(company.total_geolocations) || 0;
      monthlyAggregates[key].uniqueShipments += Number(company.total_unique_shipments) || 0;
    });
  });

  return Object.values(monthlyAggregates).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.monthNumber - b.monthNumber;
  });
};

const SortableHeadCell: React.FC<{
  column: OrderBy;
  label: string;
  orderBy: OrderBy;
  order: Order;
  align?: 'right';
  defaultDirection?: Order;
  onRequestSort: (column: OrderBy) => void;
}> = ({ column, label, orderBy, order, align, defaultDirection = 'asc', onRequestSort }) => {
  const active = orderBy === column;
  return (
    <TableCell align={align} sortDirection={active ? order : false}>
      <TableSortLabel
        active={active}
        direction={active ? order : defaultDirection}
        onClick={() => onRequestSort(column)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
};

const RankingTableHead: React.FC<{
  orderBy: OrderBy;
  order: Order;
  onRequestSort: (column: OrderBy) => void;
}> = ({ orderBy, order, onRequestSort }) => {
  const shared = { orderBy, order, onRequestSort };
  return (
    <TableHead>
      <TableRow>
        <SortableHeadCell column="rank" label="Rank" {...shared} />
        <SortableHeadCell column="company_name" label="Company" {...shared} />
        <SortableHeadCell column="licence_type" label="License" {...shared} />
        <SortableHeadCell column="total_transactions" label="Transactions" align="right" defaultDirection="desc" {...shared} />
        <SortableHeadCell column="total_geolocations" label="Geolocations" align="right" defaultDirection="desc" {...shared} />
        <SortableHeadCell column="total_unique_shipments" label="Unique Shipments" align="right" defaultDirection="desc" {...shared} />
        <SortableHeadCell column="disabled" label="Status" defaultDirection="desc" {...shared} />
        <SortableHeadCell column="partner_name" label="Partner" {...shared} />
      </TableRow>
    </TableHead>
  );
};

const CompanyRankingRow: React.FC<{ company: RankedCompany }> = ({ company }) => (
  <TableRow
    hover
    sx={{ cursor: 'pointer' }}
    onClick={() => globalThis.location.href = `/statistics/company/${company.company_id}`}
  >
    <TableCell>
      <Chip
        label={`#${company.rank}`}
        size="small"
        color={company.rank <= 3 ? 'primary' : 'default'}
      />
    </TableCell>
    <TableCell>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        ({company.company_id}) {company.company_name}
      </Typography>
    </TableCell>
    <TableCell>
      <Chip label={company.licence_type} size="small" variant="outlined" />
    </TableCell>
    <TableCell align="right">
      <Typography variant="body2">
        {formatNumber(company.total_transactions)}
      </Typography>
      {company.shipment_limit && (
        <Typography variant="caption" color="text.secondary">
          of {formatNumber(company.shipment_limit)}
        </Typography>
      )}
    </TableCell>
    <TableCell align="right">
      <Typography variant="body2">
        {formatNumber(company.total_geolocations)}
      </Typography>
      {company.geo_limit && (
        <Typography variant="caption" color="text.secondary">
          of {formatNumber(company.geo_limit)}
        </Typography>
      )}
    </TableCell>
    <TableCell align="right">
      {formatNumber(company.total_unique_shipments)}
    </TableCell>
    <TableCell>
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {company.is_demo && (
          <Chip label="Demo" size="small" color="warning" />
        )}
        <Chip
          label={company.disabled ? 'Inactive' : 'Active'}
          size="small"
          color={company.disabled ? 'default' : 'success'}
        />
      </Box>
    </TableCell>
    <TableCell>
      <Typography variant="body2" color="text.secondary">
        {company.partner_name || '-'}
      </Typography>
    </TableCell>
  </TableRow>
);

const StatisticsOverviewPage: React.FC = () => {
  const [data, setData] = useState<StatisticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    filterMode, setFilterMode,
    selectedYear, setSelectedYear,
    fromDate, setFromDate,
    toDate, setToDate,
    availableYears, setAvailableYears,
    exporting, setExporting,
  } = useDateRangeFilter();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('total_transactions');
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter | null>(null);

  useEffect(() => {
    // Generate available years (current year and 2 years back)
    const currentYear = new Date().getFullYear();
    const years = [currentYear, currentYear - 1, currentYear - 2];
    setAvailableYears(years);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, fromDate, toDate, filterMode, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const options: any = { includeDaily: true, ...buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear) };
      if (statusFilter) options.statusFilter = statusFilter;

      const result = await getStatisticsOverview(options);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics overview');
      console.error('Error fetching statistics overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    if (orderBy === property) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(property);
      // Sensible default direction per field type: asc for rank/name/text, desc for numbers/status.
      const ascDefaults: OrderBy[] = ['rank', 'company_name', 'licence_type', 'partner_name'];
      setOrder(ascDefaults.includes(property) ? 'asc' : 'desc');
    }
  };

  const getExportOptions = () => {
    const options: any = { ...buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear) };
    if (statusFilter) options.statusFilter = statusFilter;
    return options;
  };

  const handleExcelExport = async () => {
    try {
      setExporting(true);
      const downloadUrl = await exportStatisticsToExcel(getExportOptions());
      if (downloadUrl) {
        globalThis.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Error exporting to Excel:', err);
      setError(err.message || 'Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handlePdfExport = async () => {
    try {
      setExporting(true);
      const downloadUrl = await exportStatisticsToPdf(getExportOptions());
      if (downloadUrl) {
        globalThis.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Error exporting to PDF:', err);
      setError(err.message || 'Failed to export to PDF');
    } finally {
      setExporting(false);
    }
  };

  const rankedCompanies = rankCompanies(data?.companies);

  const sortedCompanies = [...rankedCompanies].sort((a, b) =>
    compareCompanies(a, b, orderBy, order),
  );

  // Aggregate monthly data for chart
  const getChartData = (): MonthlyChartData[] => aggregateMonthlyChartData(data?.monthly);

  // Transform daily data for chart
  const getDailyChartData = (): DailyChartData[] => buildStatsDailyChartData(data?.daily);

  if (!loading && error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!loading && !data) {
    return (
      <Box p={3}>
        <Alert severity="info">No data available for the selected year</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <PageHeader
          description="Platform-wide statistics across all licensees."
          actions={
            <ExportButtonMenu
              onRefresh={fetchData}
              onExcelExport={handleExcelExport}
              onPdfExport={handlePdfExport}
              exporting={exporting}
            />
          }
        />

        {/* Filters Bar */}
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          {/* Period Selection + Date Selector */}
          <PeriodFilterToggle
            filterMode={filterMode}
            onFilterModeChange={setFilterMode}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            years={availableYears}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            yearSelectorSx={{ minWidth: 250 }}
          />

          <Divider orientation="vertical" flexItem />

          {/* Status Filter */}
          <StatusFilterDropdown
            value={statusFilter}
            onChange={setStatusFilter}
            buttonHeight={48}
            buttonGap={1}
            menuItemPy={1.1}
          />
        </Paper>
      </Box>

      {/* Summary Cards */}
      {loading || !data ? (
        <StatCardsSkeleton count={4} mdCols={3} lines={2} />
      ) : (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Transactions"
              value={data.aggregated.totalTransactions}
              icon={<Assessment />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Geolocations"
              value={data.aggregated.totalGeolocations}
              icon={<LocationOn />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unique Shipments"
              value={data.aggregated.totalUniqueShipments}
              icon={<LocalShipping />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="% of Rejects"
              value={formatRejectRate(data.aggregated.totalErroredShipments, data.aggregated.totalTransactions)}
              icon={<Block />}
              color="#d32f2f"
            />
          </Grid>
          {/* Hidden: Active Licensees card — kept for potential future use
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Licensees"
              value={data.companies.filter(c => !c.disabled).length}
              // subtitle={`of ${data.companies.length} total`}
              icon={<Business />}
              color="#9c27b0"
            />
          </Grid>
          */}
        </Grid>
      )}

      {/* Statistics Trend Chart */}
      {loading || !data ? (
        <StatsChartSkeleton />
      ) : (
        <StatisticsChart
          data={getChartData()}
          dailyData={getDailyChartData()}
          title="Statistics Trend"
          height={350}
        />
      )}

      {/* Company Rankings Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Company Rankings
        </Typography>
        <TableContainer>
          <Table>
            <RankingTableHead orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
            <TableBody>
              {loading && ['rs0', 'rs1', 'rs2', 'rs3', 'rs4', 'rs5', 'rs6', 'rs7'].map((key, idx) => (
                <StatsRankingSkeletonRow key={key} idx={idx} />
              ))}
              {!loading && sortedCompanies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && sortedCompanies.map((company) => (
                <CompanyRankingRow key={company.company_id} company={company} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default StatisticsOverviewPage;
